import React, { useState, useEffect, useCallback } from 'react';
import { Question, FailureReason } from '../types';
import { submitAttempt } from '../lib/api';
import { useTriageTimer } from '../hooks/useTriageTimer';
import { QuestionPresentation } from './triage/QuestionPresentation';
import { TriageInputForm } from './triage/TriageInputForm';
import { TaxonomicModal } from './TaxonomicModal';
import { FailureModal } from './FailureModal';
import { FormulaSolverDrawer } from './FormulaSolverDrawer';
import { Clock, Compass, Play, Pause } from 'lucide-react';
import 'katex/dist/katex.min.css';

interface TriageCardProps {
  question: Question;
  onNextQuestion: () => void;
}

export const TriageCard: React.FC<TriageCardProps> = ({ question, onNextQuestion }) => {
  const [inputs, setInputs] = useState<string>('');
  const [target, setTarget] = useState<string>('');
  const [theory, setTheory] = useState<string>('');
  const [formula, setFormula] = useState<string>('');
  const [revealed, setRevealed] = useState<boolean>(false);
  const [failureReason, setFailureReason] = useState<FailureReason | null>(null);

  const [failureModalOpen, setFailureModalOpen] = useState<boolean>(false);
  const [solverDrawerOpen, setSolverDrawerOpen] = useState<boolean>(false);

  const handleFinalize = useCallback(
    async (reason?: FailureReason | null) => {
      setRevealed(true);
      setFailureReason(reason || null);

      await submitAttempt({
        questionId: question.id,
        timeSpentSeconds: 180 - secondsLeft,
        identifiedTheory: !reason,
        selectedCorrectFormula: !reason,
        failureReason: reason || null,
        userInputs: inputs,
        userTarget: target,
        userTheory: theory,
        userFormula: formula,
      });
    },
    [question.id, inputs, target, theory, formula]
  );

  const handleExpire = useCallback(() => {
    handleFinalize('FORGOT_FORMULA');
  }, [handleFinalize]);

  const {
    secondsLeft,
    timerActive,
    setTimerActive,
    resetTimer,
    formattedTime,
  } = useTriageTimer({
    initialSeconds: 180,
    revealed,
    onExpire: handleExpire,
  });

  // Reset timer & form on new question
  useEffect(() => {
    resetTimer();
    setInputs('');
    setTarget('');
    setTheory('');
    setFormula('');
    setRevealed(false);
    setFailureReason(null);
  }, [question.id, resetTimer]);

  const handleInputChange = (
    field: 'inputs' | 'target' | 'theory' | 'formula',
    value: string
  ) => {
    if (!timerActive && !revealed) setTimerActive(true);
    if (field === 'inputs') setInputs(value);
    else if (field === 'target') setTarget(value);
    else if (field === 'theory') setTheory(value);
    else if (field === 'formula') setFormula(value);
  };

  const isHighWeight = question.weight >= 5;

  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="rounded-lg border border-paper-300 bg-paper-100 p-6 shadow-sm dark:border-study-line dark:bg-study-panel">
        {/* Header: Subject, Source & 3-min Timer */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-paper-200 pb-3 dark:border-study-line">
          <div className="flex items-center gap-2">
            <span
              className={`font-mono text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded border ${
                isHighWeight
                  ? 'border-indigo-600 bg-indigo-50 text-indigo-700 dark:border-indigo-500 dark:bg-indigo-950/40 dark:text-indigo-300'
                  : 'border-paper-300 bg-paper-100 text-zinc-700 dark:border-study-line dark:bg-study-card dark:text-zinc-300'
              }`}
            >
              {question.subject} (Peso {question.weight})
            </span>
            <span className="font-mono text-xs text-zinc-500 dark:text-zinc-400">
              {question.source} — Q{question.questionNumber || '—'}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setSolverDrawerOpen(true)}
              className="flex items-center gap-1.5 rounded border border-paper-300 bg-paper-50 px-2.5 py-1 text-xs font-semibold text-zinc-700 hover:bg-paper-200 dark:border-study-line dark:bg-study-card dark:text-zinc-300 dark:hover:bg-study-line"
            >
              <Compass className="h-3.5 w-3.5 text-indigo-500" />
              Consultar Grafo
            </button>

            {!timerActive ? (
              <button
                type="button"
                onClick={() => setTimerActive(true)}
                className="flex items-center gap-1.5 font-mono text-xs font-bold px-2.5 py-1 rounded border border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:border-indigo-900/50 dark:bg-indigo-950/40 dark:text-indigo-300 transition"
              >
                <Play className="h-3 w-3 fill-current" />
                Iniciar Cronômetro ({formattedTime})
              </button>
            ) : (
              <div
                className={`flex items-center gap-1.5 font-mono text-sm font-bold px-2.5 py-1 rounded border ${
                  secondsLeft < 30
                    ? 'border-red-500 bg-red-50 text-red-600 animate-pulse dark:bg-red-950/40 dark:text-red-400'
                    : secondsLeft < 60
                    ? 'border-amber-500 bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400'
                    : 'border-paper-300 bg-paper-50 text-zinc-800 dark:border-study-line dark:bg-study-card dark:text-zinc-200'
                }`}
              >
                <Clock className="h-4 w-4" />
                <span>{formattedTime}</span>
                <button
                  type="button"
                  onClick={() => setTimerActive(false)}
                  title="Pausar cronômetro"
                  className="ml-1 text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200"
                >
                  <Pause className="h-3 w-3" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Question Statement, Diagram & Alternatives */}
        <QuestionPresentation question={question} />

        {/* Adaptive Triage Inputs or Taxonomic Master Reveal */}
        {!revealed ? (
          <TriageInputForm
            question={question}
            inputs={inputs}
            target={target}
            theory={theory}
            formula={formula}
            onInputChange={handleInputChange}
            onSubmit={() => handleFinalize(null)}
            onStuck={() => setFailureModalOpen(true)}
          />
        ) : (
          <div className="mt-6 border-t border-paper-200 pt-5 dark:border-study-line">
            <TaxonomicModal
              question={question}
              userSubmission={{
                inputs,
                target,
                theory,
                formula,
                failureReason,
              }}
              onNextQuestion={onNextQuestion}
            />
          </div>
        )}
      </div>

      <FailureModal
        isOpen={failureModalOpen}
        onClose={() => setFailureModalOpen(false)}
        onSelectReason={(reason) => handleFinalize(reason)}
      />

      <FormulaSolverDrawer
        isOpen={solverDrawerOpen}
        onClose={() => setSolverDrawerOpen(false)}
        onApplyFormula={(latex) => setFormula(latex)}
      />
    </div>
  );
};
